import React, { useId, useState } from 'react'
import styled from '@emotion/styled'

import { ecard } from 'models/StudioCampaign'
import Row from 'modules/ui/atoms/Layout/Row'
import Label from '../../../ui/atoms/Typography/Label'
import { ToggleSwitch } from '../../../ui/atoms/ToggleSwitch'
import { DedicationFormData } from './order-dedication-drawer-panel'
import OrderDedicationRecipientFields from './order-dedication-recipient-fields'
import OrderDedicationAddressFields from './order-dedication-address-fields'

import { FieldErrors, UseFormSetValue, UseFormUnregister, UseFormWatch } from 'react-hook-form'
import EcardSelector from '../designation/EcardSelector'
import { Checkbox } from '../../../ui/atoms/Checkbox'
import { UiIcon } from 'modules/ui/atoms/Icon'
import { useTranslation } from 'next-i18next'

export type EcardInfo = {
  id?: number
  src: string
}

type RecipientOptionsType = {
  errors: FieldErrors
  watch: UseFormWatch<DedicationFormData>
  setValue: UseFormSetValue<DedicationFormData>
  unregister: UseFormUnregister<DedicationFormData>
  isECardEnabled: boolean
  isEmailEnabled: boolean
  isPrintedCardEnabled: boolean
  ecardInfo: EcardInfo
  cardList: ecard[]
  isEmailToggled: boolean
  isPrintCardToggled: boolean
}

const StyledRow = styled(Row)`
  height: 44px;
  margin-bottom: 20px;
  align-content: center;
  justify-content: space-between;
  padding: 0 16px;
`

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border-radius: 8px;
  gap: 8px;

  margin-top: 16px;
  padding: 8px 16px;
  border: none;
  background-color: #f8f7f7;

  cursor: pointer;
  color: #333;

  &:hover {
    background-color: #e0e0e0;
  }
`

const PreviewImage = styled.div`
  margin-top: 16px;
  border-radius: 8px;
  text-align: center;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
`

const PreviewButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;

  font-size: 14px;
  color: #425ccd;
`

const RecipientOptions = (props: RecipientOptionsType) => {
  const {
    errors,
    watch,
    isECardEnabled,
    isEmailEnabled,
    isPrintedCardEnabled,
    cardList,
    ecardInfo: ecardData,
    isEmailToggled: isEmailToggleChecked,
    isPrintCardToggled,
    setValue,
    unregister,
  } = props

  const { t } = useTranslation()

  const EcardObj = ecardData ?? {}

  const [isPrintCardToggleChecked, setIsPrintCardToggleChecked] = useState(isPrintCardToggled)
  const [isEcardToggled, setEcardToggled] = useState(!!ecardData?.id)
  const [ecardInfo, setSelectedEcardInfo] = useState(EcardObj)
  const id = useId()
  const [isEcardChecked, setIsEcardChecked] = useState(false)

  const [isPreviewVisible, setIsPreviewVisible] = useState(false)

  const handleEmailNotificationToggleChange = () => {
    if (isEmailToggleChecked) {
      unregister(
        ['recipientEmailAddress', 'recipientFirstName', 'recipientLastName', 'ecardMessage'],
        {
          keepDefaultValue: true,
          keepValue: false,
        },
      )
    }

    setValue('isEmailNotification', !isEmailToggleChecked)
  }

  const handlePrintCardToggleChange = () => {
    if (isPrintCardToggleChecked) {
      unregister(
        [
          'recipientFirstName',
          'recipientLastName',
          'recipientAddress',
          'recipientCity',
          'recipientState',
          'recipientPostalCode',
          'recipientCountry',
        ],
        { keepDefaultValue: true, keepValue: false },
      )
    }

    setIsPrintCardToggleChecked(!isPrintCardToggleChecked)

    setValue('isPrintCard', !isPrintCardToggleChecked)
  }

  const handleCardSelect = (ecardInfo: ecard) => {
    setSelectedEcardInfo({ id: ecardInfo.id, src: ecardInfo.image_url })

    setValue('ecardId', ecardInfo?.id)
  }

  const onEcardToggeled = (isToggeled: boolean) => {
    if (isEcardToggled) {
      unregister(['ecardId'])
    }

    setEcardToggled(isToggeled)
  }

  const handlePreviewToggle = () => {
    setIsPreviewVisible(!isPreviewVisible)
  }

  return (
    <div>
      <Row style={{ marginBottom: '8px' }}>
        {isEmailEnabled && (
          <Checkbox
            checked={isEmailToggleChecked}
            onChange={handleEmailNotificationToggleChange}
            dataId="dedication-send-email-checkbox"
          >
            {t('orderDedicationOptions.sendEmail.label', 'Send an email')}
          </Checkbox>
        )}

        {isPrintedCardEnabled && (
          <Checkbox
            onChange={handlePrintCardToggleChange}
            checked={isPrintCardToggleChecked}
            dataId="dedication-send-printed-card-checkbox"
          >
            {t('orderDedicationOptions.sendPrintedCard.label', 'Send a printed card')}
          </Checkbox>
        )}
      </Row>

      <Row style={{ marginBottom: '8px' }}>
        {isEmailToggleChecked || isPrintCardToggleChecked ? (
          <OrderDedicationRecipientFields
            isEmailToggleChecked={isEmailToggleChecked}
            errors={errors}
          />
        ) : null}
      </Row>

      {isECardEnabled && isEmailToggleChecked && (
        <StyledRow>
          <Label id={id}>
            {t('orderDedicationOptions.includeEcard.label', 'Include an Ecard')}
          </Label>
          <ToggleSwitch
            ariaLabelledBy={id}
            checked={isEcardChecked}
            dataId="dedication-include-ecard-toggle"
            onChange={() => setIsEcardChecked(!isEcardChecked)}
          />
        </StyledRow>
      )}

      {isEcardChecked && isEmailToggleChecked && (
        <>
          <EcardSelector
            ecardId={ecardInfo?.id}
            label={t('orderDedicationOptions.ecardSelector.label', 'Include an Ecard')}
            cardList={cardList}
            radioGroupName="ecardId"
            onCardSelect={handleCardSelect}
            onEcardToggled={onEcardToggeled}
          />

          {ecardInfo?.src ? (
            <PreviewButton type="button" onClick={handlePreviewToggle}>
              {isPreviewVisible ? (
                <PreviewButtonContainer>
                  <UiIcon name="visibility_off" />
                  <span
                    aria-label={t('orderDedicationOptions.previewButton.hideAriaLabel', 'hide')}
                  >
                    {t('orderDedicationOptions.previewButton.hidePreview', 'Hide preview')}
                  </span>
                </PreviewButtonContainer>
              ) : (
                <PreviewButtonContainer>
                  <UiIcon name="visibility" />
                  <span
                    aria-label={t('orderDedicationOptions.previewButton.showAriaLabel', 'show')}
                  >
                    {t('orderDedicationOptions.previewButton.showPreview', 'Show preview')}
                  </span>
                </PreviewButtonContainer>
              )}
            </PreviewButton>
          ) : null}

          {/* Conditionally render the preview image */}
          {isPreviewVisible && ecardInfo?.src ? (
            <PreviewImage>
              <img
                src={ecardInfo.src}
                alt={t('orderDedicationOptions.previewImage.alt', 'Ecard Preview')}
              />
            </PreviewImage>
          ) : null}
        </>
      )}
      {isPrintCardToggleChecked && <OrderDedicationAddressFields errors={errors} watch={watch} />}
    </div>
  )
}

export default RecipientOptions
